import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'

import type { Asset } from 'contentful'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import DemoForm from '@segment/templates/Forms/DemoForm'

import { List } from './List'
import { MobileListCarousel } from './MobileListCarosuel'

const copy = {
  overline: `Customers`,
  heading: `The most innovative companies choose Segment`,
  description: `Tens of thousands of companies, including Intuit, FOX, IBM, and Levi’s choose Twilio Segment to empower their teams with good data to make real-time decisions, accelerate growth and deliver world-class customer experiences.`,
  filtersTitle: `Discover what you are looking for:`,
  buttonText: `Get a demo`,
}

export type Card = {
  featuredImage?: Asset
  featuredLogo?: Asset
  slug?: string
}

interface HeroProps {
  data: Card[]
}

const Hero: FC<PropsWithChildren<HeroProps>> = ({ data, children }) => {
  return (
    <Background>
      <Wrapper>
        <Copy>
          <StyledCopyTextBase centered={true}>
            <CopyText.Overline variant="h5" tag="h1">
              {copy.overline}
            </CopyText.Overline>
            <CopyText.Heading variant="h1" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pExtraLarge">{copy.description}</CopyText.Description>
            <StyledDemoForm
              isProgressive={true}
              customFormIdentifier="site-customers-demo"
              successMessageAppearance="twilioLight"
            />
          </StyledCopyTextBase>
        </Copy>
        <Featured>
          <List data={data} />
          <MobileListCarousel data={data} />
        </Featured>
      </Wrapper>
      <FilterTitle variant="pMedium" tag="p">
        {copy.filtersTitle}
      </FilterTitle>
      <Filters>{children}</Filters>
    </Background>
  )
}

const FilterTitle = styled(Typography)`
  text-align: center;
  margin-bottom: 40px;
  margin-top: 0;
`

const Filters = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1040px;
  padding: 0 24px;
  margin: 0 auto 72px auto;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 0 40px;
  }

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    margin-bottom: 88px;
  }
`

const StyledDemoForm = styled(DemoForm)`
  margin: 40px auto 0;
  max-width: 420px;

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    margin: 40px 0 0;
  }
`

const StyledCopyTextBase = styled(CopyTextBase)`
  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    text-align: left;
  }
`

const Background = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.palette.neutrals.white[100]};
  border-radius: 0px 0px 0px 80px;

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    border-radius: 0px 0px 0px 150px;
  }
`

const Wrapper = styled.div`
  display: grid;
  gap: 55px;
  padding: 40px 0 48px;
  grid-template-areas:
    'copy'
    'featured';
  justify-content: center;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 40px 0 54px;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding: 40px 40px 54px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    gap: 80px;
    padding-bottom: 88px;
  }

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    gap: 120px;
    padding: 80px 80px 88px;
    grid-template-columns: minmax(0%, 480px) minmax(0%, 444px);
    grid-template-areas: 'copy featured';
  }

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    padding-bottom: 100px;
    padding-right: 42px;
    grid-template-columns: minmax(0%, 560px) minmax(0%, 678px);
    gap: 80px;
  }
`

const Copy = styled.div`
  grid-area: copy;
  padding: 0 24px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 0 40px;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding: 0;
  }
`

const Featured = styled.div`
  grid-area: featured;
  overflow-x: hidden;
`

export default Hero
